import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, Phone, UtensilsCrossed, MapPin, Clock } from 'lucide-react';
import Button from '../ui/Button';
import ThemeToggle from '../ui/ThemeToggle';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import { useLanguage } from '../../context/LanguageContext';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.menu'), href: '/menu' },
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.gallery'), href: '/gallery' },
    { name: t('nav.contact'), href: '/contact' }
  ];

  return (
    <header className="bg-white dark:bg-gray-900 shadow-sm sticky top-0 z-50 safe-area-top">
      {/* Top bar */}
      <div className="bg-primary-800 dark:bg-primary-900 text-primary-100 py-2.5 safe-area-x">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-sm">
            <div className="hidden sm:flex items-center gap-4 text-primary-300">
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                München
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Di-So 12:00-23:00
              </span>
            </div>
            <div className="flex items-center gap-4 sm:gap-6 w-full sm:w-auto justify-between sm:justify-end">
              <a
                href="tel:+4989987654"
                className="flex items-center gap-2 hover:text-accent-400 transition-colors min-h-[44px] px-2 -mx-2 font-medium"
              >
                <Phone className="h-4 w-4" />
                <span className="hidden xs:inline">089 - 987 654 32</span>
                <span className="xs:hidden">{t('header.call')}</span>
              </a>
              <Link
                to="/reservation"
                className="flex items-center gap-2 bg-accent-600 hover:bg-accent-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold transition-colors min-h-[32px]"
              >
                <span>{t('nav.reserveTable')}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-18 py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="bg-gradient-to-br from-primary-600 to-primary-700 p-2.5 rounded-xl shadow-lg shadow-primary-600/20 group-hover:shadow-primary-600/40 transition-shadow">
                <UtensilsCrossed className="h-6 w-6 text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-accent-500 rounded-full border-2 border-white dark:border-gray-900" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-display font-bold text-primary-800 dark:text-primary-100 leading-tight">Bella Vista</span>
              <span className="text-sm font-medium text-accent-600 dark:text-accent-400 -mt-0.5">Ristorante Italiano</span>
            </div>
          </Link>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `text-base font-medium transition-colors ${
                    isActive
                      ? 'text-accent-500'
                      : 'text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400'
                  }`
                }
              >
                {item.name}
              </NavLink>
            ))}
          </div>

          {/* Right side actions */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            <ThemeToggle />
            <Button to="/reservation" size="sm">
              {t('nav.reserveTable')}
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 active:bg-gray-200 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? t('nav.menuClose') : t('nav.menuOpen')}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile navigation */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="md:hidden fixed inset-0 bg-black/50 z-40 animate-fade-in"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Drawer */}
            <div className="md:hidden fixed top-0 right-0 bottom-0 w-full max-w-xs bg-white dark:bg-gray-900 z-50 shadow-2xl animate-slide-in-right overflow-y-auto safe-area-right">
              {/* Drawer header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
                <span className="text-lg font-semibold text-gray-900 dark:text-white">Menu</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label={t('nav.menuClose')}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Navigation links */}
              <div className="flex flex-col p-4 gap-1">
                {navigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `px-4 py-3.5 rounded-lg text-base font-medium transition-colors min-h-[48px] flex items-center ${
                        isActive
                          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 active:bg-gray-100'
                      }`
                    }
                  >
                    {item.name}
                  </NavLink>
                ))}

                {/* Reservation Link */}
                <NavLink
                  to="/reservation"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3.5 rounded-lg text-base font-medium text-accent-600 dark:text-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/30 flex items-center gap-2 min-h-[48px] mt-2 border-t border-gray-100 dark:border-gray-800 pt-4"
                >
                  {t('nav.reservation')}
                </NavLink>
              </div>

              {/* Bottom section */}
              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 safe-area-bottom">
                {/* Mobile controls */}
                <div className="flex items-center gap-3 mb-4">
                  <LanguageSwitcher className="flex-1" />
                  <ThemeToggle />
                </div>

                <Button to="/reservation" className="w-full" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav.reserveTable')}
                </Button>
              </div>
            </div>
          </>
        )}
      </nav>
    </header>
  );
}
